import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  computed,
  effect,
  HostListener,
  inject,
  Injector,
  input,
  OnDestroy,
  output,
  signal,
  Type,
  ViewChild,
  viewChild,
  ViewContainerRef
} from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MAT_AUTOCOMPLETE_DEFAULT_OPTIONS } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MAT_MENU_DEFAULT_OPTIONS, MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  CUSTOM_FILTER_ENGINE_DATE_PICKER_OVERLAY_PANEL_CSS_CLASS,
  CUSTOM_FILTER_ENGINE_OVERLAY_PANEL_CSS_CLASS,
  getMenuPanelElement,
  getMenuTriggerElement,
  hasParentWithClass,
  isClickOutsideElement
} from '@iot-platform/iot-platform-utils';
import { Filter } from '@iot-platform/models/common';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { isFieldOfTypeList } from '../../helpers';
import { FilterFieldComponentInstance, FilterFieldType, FilterFieldV2 } from '../../models';
import { FilterEngineV2Store } from '../../state/filter-engine-v2.store';
import {
  FilterDateFieldComponent,
  FilterDropdownFieldComponent,
  FilterNumberFieldComponent,
  FilterSetFieldComponent,
  FilterTextFieldComponent,
  FilterTimezoneFieldComponent,
  FilterTreeFieldComponent
} from '../fields';

@Component({
  selector: 'iot-platform-ui-filter-engine-field-trigger',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, TranslatePipe, FlexLayoutModule, MatMenu, MatMenuTrigger, NgClass],
  templateUrl: 'filter-field-trigger.component.html',
  styleUrls: ['./filter-field-trigger.component.scss'],
  providers: [
    {
      provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
      useValue: { overlayPanelClass: CUSTOM_FILTER_ENGINE_OVERLAY_PANEL_CSS_CLASS }
    },
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { overlayPanelClass: CUSTOM_FILTER_ENGINE_OVERLAY_PANEL_CSS_CLASS }
    },
    {
      provide: MAT_MENU_DEFAULT_OPTIONS,
      useValue: { overlayPanelClass: CUSTOM_FILTER_ENGINE_OVERLAY_PANEL_CSS_CLASS }
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterFieldTriggerComponent implements AfterViewInit, OnDestroy {
  field = input<FilterFieldV2>();
  selectFavoriteField = output<FilterFieldV2>();
  filterApplied = output<{
    filter: Filter;
    field: FilterFieldV2;
  }>();

  canApply = signal(false);
  menuOpened = signal(false);
  requiresValue = signal(false);
  ofTypeList = computed(() => isFieldOfTypeList(this.field() as FilterFieldV2));
  @ViewChild(MatMenuTrigger) menuRef!: MatMenuTrigger;
  readonly store = inject(FilterEngineV2Store);
  isAutoApply = this.store.autoApply;
  readonly currentFilters = computed(() => {
    const field = this.field();
    const getFiltersForField = this.store.getFiltersForField();
    return field ? getFiltersForField(field?.options?.criteriaKey as string) : [];
  });
  canReset = computed(() => {
    const currentFilters = this.currentFilters();
    return currentFilters?.length;
  });
  readonly closeOnClickOutside = this.store.closeOnClickOutside;
  readonly disabled = this.store.disabled;
  readonly fieldContainerRef = viewChild('fieldContainer', { read: ViewContainerRef });
  currentComponentRef: ComponentRef<FilterFieldComponentInstance> | null = null;
  menuId = '';
  readonly destroy$ = new Subject<void>();
  isProgressClick = false;
  protected readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly injector: Injector = inject(Injector);

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isProgressClick) {
      return;
    }

    if (!this.shouldHandleClickOutside()) {
      return;
    }

    const triggerElement = getMenuTriggerElement(this.menuRef);
    const menuPanelElement = getMenuPanelElement(this.menuRef);
    if (!menuPanelElement || !triggerElement) {
      return;
    }

    const clickedElement = event.target as HTMLElement;
    const isClickedOutsideMenu = isClickOutsideElement(event, menuPanelElement);
    const isInsideMenuOverlay = hasParentWithClass(clickedElement, CUSTOM_FILTER_ENGINE_OVERLAY_PANEL_CSS_CLASS);
    const datepickerPopup = document.querySelector(`.${CUSTOM_FILTER_ENGINE_DATE_PICKER_OVERLAY_PANEL_CSS_CLASS}`);
    const isInsideDatePickerOverlay = !!datepickerPopup && !isClickOutsideElement(event, datepickerPopup as HTMLElement);

    const isDatePickerField = this.field()?.fieldType === FilterFieldType.DATE;

    if (isClickedOutsideMenu && !isInsideMenuOverlay && (isDatePickerField ? !isInsideDatePickerOverlay : true)) {
      this.closeMenu();
    }
  }

  ngAfterViewInit() {
    const fieldConfig = this.field();
    this.menuId = fieldConfig?.key || `menu-${Math.random().toString(36)}`;

    if (this.menuRef) {
      this.menuRef.menuOpened.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.menuOpened.set(true);
        this.store.notifyMenuOpened(this.menuId);

        this.isProgressClick = true;
        this.cdr.detectChanges();
        const timeout = setTimeout(() => {
          this.isProgressClick = false;
          clearTimeout(timeout);
        }, 100);
      });

      this.menuRef.menuClosed.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.menuOpened.set(false);
        this.isProgressClick = false;
      });
    }

    effect(
      () => {
        const currentOpenMenuId = this.store.currentOpenMenuId();
        if (currentOpenMenuId && currentOpenMenuId !== this.menuId && this.menuRef?.menuOpen) {
          this.menuRef.closeMenu();
        }
      },
      { injector: this.injector }
    );

    effect(
      () => {
        const isMenuOpen = this.menuOpened();
        const field = this.field();

        if (isMenuOpen && field) {
          const timeout = setTimeout(() => {
            this.createFieldComponent(field);
            clearTimeout(timeout);
          }, 0);
        } else if (!isMenuOpen) {
          this.clearFieldComponent();
        }
      },
      { injector: this.injector }
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearFieldComponent();
  }

  onFilterApplied(event: { filter: Filter; field: FilterFieldV2 }): void {
    this.filterApplied.emit(event);

    const field = this.field();
    if (this.store.autoApply() && (field?.options as any)?.closeOnApply) {
      this.menuRef?.closeMenu();
    }
  }

  getFieldComponent(fieldType: FilterFieldType): Type<any> | null {
    switch (fieldType) {
      case FilterFieldType.TEXT:
        return FilterTextFieldComponent;
      case FilterFieldType.NUMBER:
        return FilterNumberFieldComponent;
      case FilterFieldType.DATE:
        return FilterDateFieldComponent;
      case FilterFieldType.TIMEZONE:
        return FilterTimezoneFieldComponent;
      case FilterFieldType.TREE_VIEW_FILTER:
        return FilterTreeFieldComponent;
      case FilterFieldType.DROPDOWN:
      case FilterFieldType.AUTOCOMPLETE:
        return FilterDropdownFieldComponent;
      case FilterFieldType.SET_FILTER:
        return FilterSetFieldComponent;
      default:
        console.warn(`Unsupported field type: ${fieldType}`);
        return null;
    }
  }

  createFieldComponent(field: FilterFieldV2): void {
    this.clearFieldComponent();

    const componentType = this.getFieldComponent(field.fieldType);
    if (!componentType) return;

    const container = this.fieldContainerRef();
    if (!container) {
      console.warn('Field container not found in menu template');
      return;
    }

    this.currentComponentRef = container.createComponent(componentType);
    this.currentComponentRef.setInput('field', field);

    const instance = this.currentComponentRef.instance as any;
    if (instance.filterApplied) {
      instance.filterApplied.subscribe((event: { filter: Filter; field: FilterFieldV2 }) => {
        this.onFilterApplied(event);
      });
    }
    if (instance.requiresValue) {
      this.requiresValue = instance.requiresValue;
    }
    if (instance.canApply) {
      instance.canApply.subscribe((canApply: boolean) => {
        this.canApply.set(canApply);
      });
    }
  }

  onReset() {
    this.currentComponentRef?.instance?.onReset?.emit?.();
  }

  onApply() {
    this.currentComponentRef?.instance?.onApply?.emit?.();
  }

  clearFieldComponent(): void {
    if (this.currentComponentRef) {
      this.currentComponentRef.destroy();
      this.currentComponentRef = null;
    }
  }

  private shouldHandleClickOutside(): boolean {
    return !this.closeOnClickOutside() && this.menuOpened();
  }

  private closeMenu(): void {
    if (this.menuRef?.menuOpen) {
      this.menuRef.closeMenu();
      this.menuOpened.set(false);
    }
  }
}
